import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, CheckCircle, AlertCircle, Info, ChevronDown, ChevronUp, Edit, Download, Printer } from 'lucide-react';
import { buildApiUrl, API_ENDPOINTS } from './utils/baseurl';
import logo from './assets/logo.png';
import SortableGrid from './components/SortableGrid';

const LoanInterestUI = () => {
  const [searchParams, setSearchParams] = useState({
    loanNumber: '',
    mobileNumber: '',
    loanHolderName: '',
    loanStatus: ''
  });
  const [showGrid, setShowGrid] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filteredLoans, setFilteredLoans] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loanDetails, setLoanDetails] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  // Add paymentType to paymentData state
  const [paymentData, setPaymentData] = useState({
    id: 0, // 0 for new payment, non-zero for edit
    loanNumber: '',
    loanID: 0,
    paymentDate: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
    paymentAmount: '',
    paymentType: '', // Will be set from domain values
  });

  // Modal state
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info', // 'success', 'error', 'info', 'warning'
    title: '',
    message: ''
  });

  // Domain values state
  const [domainValues, setDomainValues] = useState([]);
  const [domainValuesLoaded, setDomainValuesLoaded] = useState(false);

  // Payment receipt state
  const [showPaymentReceipt, setShowPaymentReceipt] = useState(false);
  const [isGeneratingPaymentPDF, setIsGeneratingPaymentPDF] = useState(false);
  const [completeLoanData, setCompleteLoanData] = useState(null);
  const paymentReceiptRef = useRef();

  // Server-side sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  // Column configuration for SortableGrid
  const loanColumns = [
    {
      key: 'loanNumber',
      label: 'Loan Number',
      sortable: true,
      className: 'font-bold text-indigo-900'
    },
    {
      key: 'loanHolderName',
      label: 'Name',
      sortable: true
    },
    {
      key: 'mobileNumber',
      label: 'Mobile',
      sortable: true
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      render: (item) => `₹${(item.loanAmount || item.amount)?.toLocaleString()}`
    },
    {
      key: 'goldWeight',
      label: 'Gold Weight',
      sortable: true,
      render: (item) => {
        const weight = parseFloat(item.goldWeight) || 0;
        return `${weight.toFixed(2)} grams`;
      }
    },
    {
      key: 'loanStartDate',
      label: 'Start Date',
      sortable: true,
      render: (item) => item.loanStartDate || 'N/A'
    },
    {
      key: 'loanDueDate',
      label: 'Due Date',
      sortable: true,
      render: (item) => {
        if (!item.loanDueDate) return 'N/A';
        
        const dueDate = new Date(item.loanDueDate);
        const today = new Date();
        const isOverdue = dueDate < today;
        
        return (
          <span className={isOverdue ? 'text-red-600 font-semibold' : 'text-gray-900'}>
            {item.loanDueDate}
            {isOverdue && <span className="ml-1 text-xs">(Overdue)</span>}
          </span>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (item) => (
        <span className={getStatusBadge(item.status)}>
          {getLoanStatusOptions().find(option => option.value === item.status)?.label || item.status}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Action',
      sortable: false,
      render: (item) => (
        <button
          className="flex items-center gap-1 px-2 md:px-4 py-1 md:py-2 bg-indigo-900 text-white rounded hover:bg-indigo-700 transition-colors text-xs md:text-sm"
          onClick={(e) => {
            e.stopPropagation();
            handleView(item);
          }}
        >
          View {expandedRow === item.id ? <ChevronUp size={12} className="md:w-4 md:h-4" /> : <ChevronDown size={12} className="md:w-4 md:h-4" />}
        </button>
      )
    }
  ];

  // Modal functions
  const showModal = (type, title, message) => {
    setModal({
      isOpen: true,
      type,
      title,
      message
    });
  };

  const closeModal = () => {
    setModal({
      isOpen: false,
      type: 'info',
      title: '',
      message: ''
    });
  };
  // Fetch loans with server-side search and sorting
  const fetchLoans = useCallback(async (filters = {}, sortKey = null, sortDirection = null) => {
    try {
      setLoading(true);

        const params = new URLSearchParams({
        pageIndex: '0',
        pageSize: '1000', // Get more results for payment search
        ...(filters.loanNumber && { LoanNumber: filters.loanNumber }),
        ...(filters.mobileNumber && { MobileNumber: filters.mobileNumber }),
        ...(filters.loanHolderName && { Name: filters.loanHolderName }),
        ...(filters.loanStatus && { Status: filters.loanStatus }),
        // Add sorting parameters
        ...(sortKey && { sortBy: sortKey }),
        ...(sortDirection && { sortDirection: sortDirection })
        });

      const response = await fetch(`${buildApiUrl(API_ENDPOINTS.LOAN_SEARCH)}?${params.toString()}`);
        const result = await response.json();

      if (result.data) {
        setFilteredLoans(result.data);
        return result.data;
      } else if (Array.isArray(result)) {
        // Handle case where API returns array directly
        setFilteredLoans(result);
        return result;
          } else {
        setFilteredLoans([]);
        return [];
      }
    } catch (error) {
      console.error('Error fetching loans:', error);
      setFilteredLoans([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch domain values from server with retry mechanism
  const fetchDomainValues = useCallback(async (retryCount = 0) => {
    const maxRetries = 3;
    
    try {
      // Get access token from localStorage
      const token = checkAccessToken();
      
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };
      
      const response = await fetch(buildApiUrl(API_ENDPOINTS.DOMAIN_VALUES), {
        method: 'GET',
        headers: headers,
        cache: 'no-cache' // Prevent caching issues
      });
      
      if (response.ok) {
        const data = await response.json();
        setDomainValues(data);
        setDomainValuesLoaded(true);
        return data;
      } else {
        // Retry if we haven't exceeded max retries
        if (retryCount < maxRetries) {
          setTimeout(() => {
            fetchDomainValues(retryCount + 1);
          }, 2000);
          return [];
        } else {
          setDomainValuesLoaded(true); // Set to true even on error to prevent infinite loading
          return [];
        }
      }
    } catch (error) {
      // Retry if we haven't exceeded max retries
      if (retryCount < maxRetries) {
        setTimeout(() => {
          fetchDomainValues(retryCount + 1);
        }, 2000);
        return [];
      } else {
        setDomainValuesLoaded(true); // Set to true even on error to prevent infinite loading
        return [];
      }
    }
  }, []);

  // Fetch domain values on component mount
  useEffect(() => {
    fetchDomainValues();
  }, [fetchDomainValues]);

  // Get loan status options from domain values (domainID: 10 for loan status)
  const getLoanStatusOptions = useCallback(() => {
    const loanStatusValues = domainValues.filter(item => item.domainID === 10 && item.isActive);
    
    // Fallback options in case API fails or returns no data
    const fallbackOptions = [
      { value: 'A', label: 'Active' },
      { value: 'C', label: 'Closed' }
    ];
    
    const options = loanStatusValues.length > 0 
      ? loanStatusValues.map(item => ({
          value: item.code,
          label: item.displayName
        }))
      : fallbackOptions;
    
    return options;
  }, [domainValues]);

  // Get payment type options from domain values (domainID: 11 for payment types)
  const getPaymentTypeOptions = useCallback(() => {
    const paymentTypeValues = domainValues.filter(item => item.domainID === 11 && item.isActive);
    
    // Fallback options in case API fails or returns no data
    const fallbackOptions = [
      { value: 'PR', label: 'Principal' },
      { value: 'IN', label: 'Interest' }
    ];
    
    const options = paymentTypeValues.length > 0 
      ? paymentTypeValues.map(item => ({
          value: item.code,
          label: item.displayName
        }))
      : fallbackOptions;
    
    return options;
  }, [domainValues]);

  // Helper function to check if access token is available
  const checkAccessToken = () => {
    const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    if (!token) {
      showModal('warning', 'Authentication Required', 'Please login to access domain values. The access token is required for this API call.');
    }
    return token;
  };

  // Fetch payment history for a specific loan
  const fetchPaymentHistory = async (loanID) => {
    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.LOAN_PAYMENT_HISTORY(loanID)));
      if (response.ok) {
        const payments = await response.json();
        setPaymentHistory(payments || []);
        return payments || [];
      } else {
        setPaymentHistory([]);
        return [];
      }
    } catch (error) {
      setPaymentHistory([]);
      return [];
    }
  };

  // Fetch loan details for total amount
  const fetchLoanDetails = async (loanID) => {
    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.LOAN_INTEREST_DETAILS(loanID)));
      if (response.ok) {
        const details = await response.json();
        setLoanDetails(details);
        return details;
      } else {
        setLoanDetails(null);
        return null;
      }
    } catch (error) {
      setLoanDetails(null);
      return null;
    }
  };

  // Handle server-side sorting
  const handleSort = (sortKey, sortDirection) => {
    setSortConfig({ key: sortKey, direction: sortDirection });
    fetchLoans(searchParams, sortKey, sortDirection);
  };

  // Server-side search handler
  const handleSearch = async () => {
    setShowGrid(true);
    await fetchLoans(searchParams, sortConfig.key, sortConfig.direction);
  };

  const handleReset = () => {
    const emptyFilters = {
      loanNumber: '',
      mobileNumber: '',
      loanHolderName: '',
      loanStatus: ''
    };
    setSearchParams(emptyFilters);
    setSortConfig({ key: null, direction: 'asc' });
    setShowGrid(false);
    setFilteredLoans([]);
    setExpandedRow(null);
    setSelectedLoan(null);
    setPaymentHistory([]);
    setLoanDetails(null);
    setPaymentSummary(null);
    setShowPaymentForm(false);
    const paymentTypeOptions = getPaymentTypeOptions();
    const defaultPaymentType = paymentTypeOptions.length > 0 ? paymentTypeOptions[0].value : '';
    
    setPaymentData({
      loanNumber: '',
      loanID: 0,
      paymentDate: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
      paymentAmount: '',
      paymentType: defaultPaymentType,
    });
  };

  // Row expand/collapse
  const handleView = async (loan) => {
    if (expandedRow === loan.id) {
      setExpandedRow(null);
      setSelectedLoan(null);
      setPaymentHistory([]);
      setLoanDetails(null);
      setPaymentSummary(null);
      return;
    }
    setExpandedRow(loan.id);
    setSelectedLoan(loan);
    await Promise.all([
      fetchPaymentHistory(loan.id),
      fetchLoanDetails(loan.id),
      fetchPaymentSummary(loan.id)
    ]);
  };

  // State for payment summary
  const [paymentSummary, setPaymentSummary] = useState(null);

  // Fetch payment summary from backend
  const fetchPaymentSummary = async (loanID) => {
    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.LOAN_PAYMENT_SUMMARY(loanID)));
      if (response.ok) {
        const summary = await response.json();
        setPaymentSummary(summary);
        return summary;
      } else {
        setPaymentSummary(null);
        return null;
      }
    } catch (error) {
      setPaymentSummary(null);
      return null;
    }
  };

  // Helper to get paid/remaining by type from backend summary
  const getPaidByType = (type) => {
    if (!paymentSummary) return 0;
    return type === 'PR' ? paymentSummary.principalPaid : paymentSummary.interestPaid;
  };

  const getRemainingByType = (type) => {
    if (!paymentSummary) return 0;
    return type === 'PR' ? paymentSummary.principalRemaining : paymentSummary.interestRemaining;
  };

  // Payment functions
  const handlePayClick = () => {
    setShowPaymentForm(true);
    const paymentTypeOptions = getPaymentTypeOptions();
    const defaultPaymentType = paymentTypeOptions.length > 0 ? paymentTypeOptions[0].value : '';
    
    setPaymentData({
      id: 0, // Reset to 0 for new payment
      loanNumber: selectedLoan?.loanNumber || '',
      loanID: selectedLoan?.id || 0,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentAmount: '',
      paymentType: defaultPaymentType
    });
  };

  // Edit payment function
  const handleEditPayment = (payment) => {
    setShowPaymentForm(true);
    setPaymentData({
      id: payment.id, // Use the payment ID for editing
      loanNumber: selectedLoan?.loanNumber || '',
      loanID: selectedLoan?.id || 0,
      paymentDate: payment.paymentDate ? new Date(payment.paymentDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      paymentAmount: payment.paymentAmount?.toString() || '',
      paymentType: payment.paymentType || ''
    });
  };

  const handleSubmitPayment = async () => {
    if (!paymentData.paymentAmount || parseFloat(paymentData.paymentAmount) <= 0) {
      showModal('warning', 'Invalid Amount', 'Please enter a valid payment amount');
      return;
    }

    try {
      setLoading(true);
      
      const paymentPayload = {
        id: paymentData.id, // Use the ID from paymentData (0 for new, actual ID for edit)
        clientID: 0,
        loanID: paymentData.loanID,
        paymentAmount: parseFloat(paymentData.paymentAmount),
        paymentDate: paymentData.paymentDate + 'T00:00:00',
        paymentType: paymentData.paymentType,
        lastActionDate: new Date().toISOString(),
        lastActionUserID: 0
      };

      const response = await fetch(buildApiUrl(API_ENDPOINTS.LOAN_PAYMENT_SAVE), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentPayload)
      });

      if (response.ok) {
        const message = paymentData.id === 0 ? 'Payment submitted successfully!' : 'Payment updated successfully!';
        showModal('success', 'Payment Successful', message);
        
        // Refresh payment history and summary
        await Promise.all([
          fetchPaymentHistory(paymentData.loanID),
          fetchPaymentSummary(paymentData.loanID)
        ]);
        
        setShowPaymentForm(false);
        const paymentTypeOptions = getPaymentTypeOptions();
        const defaultPaymentType = paymentTypeOptions.length > 0 ? paymentTypeOptions[0].value : '';
        
        setPaymentData({
          id: 0, // Reset to 0 for new payment
          loanNumber: selectedLoan?.loanNumber || '',
          loanID: selectedLoan?.id || 0,
          paymentDate: new Date().toISOString().split('T')[0],
          paymentAmount: '',
          paymentType: defaultPaymentType,
        });
      } else {
        showModal('error', 'Payment Failed', 'Failed to submit payment. Please try again.');
      }
    } catch (error) {
      showModal('error', 'Payment Failed', 'Payment submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPayment = () => {
    const paymentTypeOptions = getPaymentTypeOptions();
    const defaultPaymentType = paymentTypeOptions.length > 0 ? paymentTypeOptions[0].value : '';
    
    setPaymentData({
      id: 0, // Reset to 0 for new payment
      loanNumber: selectedLoan?.loanNumber || '',
      loanID: selectedLoan?.id || 0,
      paymentDate: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
      paymentAmount: '',
      paymentType: defaultPaymentType,
    });
  };

  const handleCancelEdit = () => {
    setShowPaymentForm(false);
    const paymentTypeOptions = getPaymentTypeOptions();
    const defaultPaymentType = paymentTypeOptions.length > 0 ? paymentTypeOptions[0].value : '';
    
    setPaymentData({
      id: 0, // Reset to 0 for new payment
      loanNumber: selectedLoan?.loanNumber || '',
      loanID: selectedLoan?.id || 0,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentAmount: '',
      paymentType: defaultPaymentType,
    });
  };

  // Payment Receipt Functions
  // Fetch complete loan details for receipt
  const fetchCompleteLoanDetails = async (loanID) => {
    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.LOAN_GET_BY_ID(loanID)));
      if (response.ok) {
        const data = await response.json();
        setCompleteLoanData(data);
        return data;
      } else {
        setCompleteLoanData(null);
        return null;
      }
    } catch (error) {
      setCompleteLoanData(null);
      return null;
    }
  };

  const handlePaymentReceipt = async () => {
    if (selectedLoan?.id) {
      await fetchCompleteLoanDetails(selectedLoan.id);
    }
    setShowPaymentReceipt(true);
  };

  const closePaymentReceipt = () => {
    setShowPaymentReceipt(false);
    setCompleteLoanData(null);
  };

  const handleDownloadPaymentPDF = async () => {
    try {
      setIsGeneratingPaymentPDF(true);
      
      const paymentReceiptElement = paymentReceiptRef.current;
      
      if (!paymentReceiptElement) {
        throw new Error('Payment receipt element not found');
      }
      
      // Import jsPDF and html2canvas dynamically
      const { jsPDF } = await import('jspdf');
      const html2canvas = await import('html2canvas');
      
      // PDF settings
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const margin = 10;
      const contentWidth = pdfWidth - (margin * 2);
      
      // Configure html2canvas options
      const canvasOptions = {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        removeContainer: false,
        imageTimeout: 15000,
        foreignObjectRendering: false,
        letterRendering: true,
        scrollX: 0,
        scrollY: 0,
        x: 0,
        y: 0
      };
      
      // Ensure element is visible
      const originalDisplay = paymentReceiptElement.style.display;
      paymentReceiptElement.style.display = 'block';
      paymentReceiptElement.style.visibility = 'visible';
      paymentReceiptElement.style.opacity = '1';
      paymentReceiptElement.style.paddingBottom = '20px';
      
      // Wait for rendering
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Generate canvas
      const canvas = await html2canvas.default(paymentReceiptElement, {
        ...canvasOptions,
        width: paymentReceiptElement.offsetWidth,
        height: paymentReceiptElement.offsetHeight + 20,
      });
      
      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error('Payment receipt canvas is empty');
      }
      
      const imgData = canvas.toDataURL('image/png', 0.95);
      
      // Calculate dimensions
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Add to PDF
      pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);
      
      // Restore original display
      paymentReceiptElement.style.display = originalDisplay;
      paymentReceiptElement.style.paddingBottom = '';
      
      // Save the PDF
      const fileName = `payment_receipt_${selectedLoan?.loanNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error generating payment PDF:', error);
      showModal('error', 'PDF Generation Failed', `Error generating PDF: ${error.message}. Please try again.`);
    } finally {
      setIsGeneratingPaymentPDF(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const handlePrintPaymentReceipt = async () => {
    if (!paymentReceiptRef.current) return;
    
    try {
      const paymentReceiptElement = paymentReceiptRef.current;
      
      if (!paymentReceiptElement) {
        throw new Error('Payment receipt element not found');
      }
      
      // Ensure element is visible
      const originalDisplay = paymentReceiptElement.style.display;
      paymentReceiptElement.style.display = 'block';
      paymentReceiptElement.style.visibility = 'visible';
      paymentReceiptElement.style.opacity = '1';
      paymentReceiptElement.style.paddingBottom = '20px';
      
      // Wait for rendering
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      
      // Get the computed styles to match exactly
      const computedStyles = window.getComputedStyle(paymentReceiptElement);
      const allStyles = [];
      
      // Get all CSS rules that apply to the element
      for (let i = 0; i < document.styleSheets.length; i++) {
        try {
          const sheet = document.styleSheets[i];
          if (sheet.cssRules) {
            for (let j = 0; j < sheet.cssRules.length; j++) {
              const rule = sheet.cssRules[j];
              if (rule.selectorText) {
                allStyles.push(rule.cssText);
              }
            }
          }
        } catch (e) {
          // Skip cross-origin stylesheets
        }
      }
      
      // Create the print HTML with exact styling
      const printHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Payment Receipt</title>
            <style>
              ${allStyles.join('\n')}
              
              /* Additional print-specific styles */
              body { 
                font-family: Arial, sans-serif; 
                margin: 0; 
                padding: 20px; 
                background: white;
              }
              .receipt-container { 
                max-width: 800px; 
                margin: 0 auto; 
                background: white;
              }
              @media print {
                body { margin: 0; }
                .receipt-container { max-width: none; }
                * { -webkit-print-color-adjust: exact !important; }
              }
            </style>
          </head>
          <body>
            <div class="receipt-container">
              ${paymentReceiptElement.outerHTML}
            </div>
          </body>
        </html>
      `;
      
      printWindow.document.write(printHTML);
      printWindow.document.close();
      
      // Wait for content to load
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      printWindow.focus();
      printWindow.print();
      
      // Restore original display
      paymentReceiptElement.style.display = originalDisplay;
      paymentReceiptElement.style.paddingBottom = '';
      
    } catch (error) {
      console.error('Error printing payment receipt:', error);
      showModal('error', 'Print Failed', `Error printing receipt: ${error.message}. Please try again.`);
    }
  };

  const calculateTotalPaid = () => {
    return paymentHistory.reduce((sum, payment) => sum + (parseFloat(payment.paymentAmount) || 0), 0);
  };

  // Function to translate payment types to Tamil
  const getPaymentTypeInTamil = (paymentType) => {
    const tamilTranslations = {
      'PR': 'அசல்',
      'IN': 'வட்டி',
      'principal': 'அசல்',
      'interest': 'வட்டி',
      'Principal': 'அசல்',
      'Interest': 'வட்டி'
    };
    return tamilTranslations[paymentType] || paymentType;
  };



  // Check if loan is fully paid using backend data
  const isLoanCompleted = paymentSummary ? paymentSummary.isLoanCompleted : false;

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Handle both ISO string and date-only formats
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid date
    return date.toLocaleDateString('en-GB');
  };

  // Get status badge styling using cached domain values
  const getStatusBadge = (status) => {
    const baseClasses = "px-3 py-1 rounded-full text-sm font-medium";
    
    // Get status info from domain values
    const statusInfo = domainValues?.find(item => item.domainID === 10 && item.code === status);
    
    // Check if status is a display name and find the corresponding code
    let statusCode = status;
    if (status === 'Active') {
      statusCode = 'A';
    } else if (status === 'Closed') {
      statusCode = 'C';
    }
    
    // Use domain values to determine badge color
    if (statusCode === 'A' || status === 'Active') {
      return `${baseClasses} bg-green-100 text-green-800`;
    } else if (statusCode === 'C' || status === 'Closed') {
      return `${baseClasses} bg-red-100 text-red-800`;
    }
    
    // Default for unknown status
    return `${baseClasses} bg-gray-100 text-gray-800`;
  };

  // Custom Modal Component
  const CustomModal = () => {
    if (!modal.isOpen) return null;

    const getModalIcon = () => {
      switch (modal.type) {
        case 'success':
          return <CheckCircle className="w-6 h-6 text-green-500" />;
        case 'error':
          return <AlertCircle className="w-6 h-6 text-red-500" />;
        case 'warning':
          return <AlertCircle className="w-6 h-6 text-yellow-500" />;
        default:
          return <Info className="w-6 h-6 text-blue-500" />;
      }
    };

    const getModalColors = () => {
      switch (modal.type) {
        case 'success':
          return 'border-green-200 bg-green-50';
        case 'error':
          return 'border-red-200 bg-red-50';
        case 'warning':
          return 'border-yellow-200 bg-yellow-50';
        default:
          return 'border-blue-200 bg-blue-50';
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className={`flex items-center justify-between p-4 border-b ${getModalColors()}`}>
            <div className="flex items-center gap-3">
              {getModalIcon()}
              <h3 className="text-lg font-semibold text-gray-800">{modal.title}</h3>
            </div>
            <button
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4">
            <p className="text-gray-700 mb-4">{modal.message}</p>
            <div className="flex justify-end">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-indigo-900 text-white rounded-md hover:bg-indigo-800 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-2 md:p-3">
      {/* Custom Modal */}
      <CustomModal />
      
      {/* Payment Receipt Modal */}
      {showPaymentReceipt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Payment Receipt</h3>
              <div className="flex gap-2">
                <button
                  onClick={handlePrintPaymentReceipt}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  <Printer size={16} />
                  Print
                </button>
                <button
                  onClick={handleDownloadPaymentPDF}
                  disabled={isGeneratingPaymentPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <Download size={16} />
                  {isGeneratingPaymentPDF ? 'Generating...' : 'Download PDF'}
                </button>
                <button
                  onClick={closePaymentReceipt}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
            
            <div className="p-6" ref={paymentReceiptRef}>
              {/* Payment Receipt Content */}
              <div className="text-center mb-6 border-b-2 border-gray-900 pb-4">
                <img src={logo} alt="Logo" className="w-62 h-24 mx-auto mb-1 rounded-xl shadow-md logo-img" />
                <h2 className="text-lg font-semibold">PAYMENT RECEIPT / செலுத்தல் ரசீது</h2>
                <div className="mt-2">
                  <div><strong>உரிமம் எண்:</strong> TN-2020250409176</div>
                  <div><strong>தொலைபேசி எண் :</strong> +918838297542</div>
                </div>
              </div>

              {/* Customer and Loan Details */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="mb-2">
                    <strong>கடன்தாரர் பெயர் :</strong> {completeLoanData?.loanHolderName || selectedLoan?.loanHolderName}
                  </div>
                  <div className="mb-2">
                    <strong>தொலைபேசி எண்:</strong> {completeLoanData?.mobileNumber || selectedLoan?.mobileNumber}
                  </div>
                  <div className="mb-2">
                    <strong>முகவரி:</strong> {completeLoanData?.address || selectedLoan?.address || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="mb-2">
                    <strong>கடன் எண்:</strong> {selectedLoan?.loanNumber}
                  </div>
                  <div className="mb-2">
                    <strong>கடன் தொகை:</strong> {formatCurrency(loanDetails?.loanAmount)}
                  </div>
                  <div className="mb-2">
                    <strong>வட்டி விகிதம்:</strong> {loanDetails?.interestRate}%
                  </div>
                  <div className="mb-2">
                    <strong>மொத்த செலுத்திய தொகை:</strong> {formatCurrency(calculateTotalPaid())}
                  </div>
                  <div className="mb-2">
                    <strong>மீதமுள்ள தொகை:</strong> {formatCurrency(paymentSummary?.totalRemaining || 0)}
                  </div>
                </div>
              </div>

              {/* Payment History Table */}
              <div className="border-2 border-gray-900 mb-6">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-900 bg-gray-100">
                      <th className="border-r border-gray-900 p-2 text-left">செலுத்திய தேதி</th>
                      <th className="border-r border-gray-900 p-2 text-center">செலுத்தல் வகை</th>
                      <th className="p-2 text-center">செலுத்திய தொகை</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map((payment, index) => (
                      <tr key={payment.id || index} className="border-b border-gray-900">
                        <td className="border-r border-gray-900 p-2">{formatDate(payment.paymentDate)}</td>
                        <td className="border-r border-gray-900 p-2 text-center">
                          {getPaymentTypeInTamil(payment.paymentType)}
                        </td>
                        <td className="p-2 text-center">{formatCurrency(payment.paymentAmount)}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-gray-900 bg-gray-100 font-bold">
                      <td className="border-r border-gray-900 p-2 text-right" colSpan={2}>மொத்த செலுத்திய தொகை:</td>
                      <td className="p-2 text-center">{formatCurrency(calculateTotalPaid())}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Summary Section */}
              <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <div className="text-sm text-gray-600">கடன் தொகை</div>
                  <div className="text-lg font-bold">{formatCurrency(paymentSummary?.loanAmount || 0)}</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-600">வட்டி தொகை</div>
                  <div className="text-lg font-bold">{formatCurrency(paymentSummary?.interestAmount || 0)}</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-600">மொத்த தொகை</div>
                  <div className="text-lg font-bold">{formatCurrency(paymentSummary?.totalAmount || 0)}</div>
                </div>
              </div>

              {/* Signature Section */}
              <div className="grid grid-cols-2 gap-12 mt-8">
                <div className="text-center">
                  <div className="border-t-2 border-gray-900 pt-2 mt-10">
                    <strong>கடன்தாரர் கையொப்பம்</strong>
                  </div>
                </div>
                <div className="text-center">
                  <div className="border-t-2 border-gray-900 pt-2 mt-10">
                    <strong>மேலாளர் கையொப்பம்</strong>
                  </div>
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="mt-6 p-4 border border-gray-300 bg-gray-50 rounded-lg">
                <div className="text-center font-bold mb-2 text-sm">விதிமுறைகள் மற்றும் நிபந்தனைகள்</div>
                <div className="text-xs text-gray-700">
                  <ul className="list-disc list-inside space-y-1">
                    <li>இந்த ரசீது கடன் கணக்கிற்கு எதிராக செலுத்தப்பட்ட தொகையின் சான்றாகும்.</li>
                    <li>இந்த ரசீதை எதிர்கால குறிப்புக்காக பாதுகாப்பாக வைத்திருங்கள்.</li>
                    <li>எந்த கேள்விகளுக்கும் எங்கள் அலுவலகத்தை தொடர்பு கொள்ளவும்.</li>
                    <li>இந்த ரசீது கணினியால் உருவாக்கப்பட்டது மற்றும் கையொப்பம் தேவையில்லை.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Search Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-indigo-800 mb-4 md:mb-6">Payment Search</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">Loan Number</label>
            <input
              type="text"
              value={searchParams.loanNumber}
              onChange={e => setSearchParams({ ...searchParams, loanNumber: e.target.value })}
              className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm md:text-base"
              placeholder="Enter loan number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number</label>
            <input
              type="tel"
              value={searchParams.mobileNumber}
              onChange={e => setSearchParams({ ...searchParams, mobileNumber: e.target.value })}
              className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm md:text-base"
              placeholder="Enter mobile number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Loan Holder Name</label>
            <input
              type="text"
              value={searchParams.loanHolderName}
              onChange={e => setSearchParams({ ...searchParams, loanHolderName: e.target.value })}
              className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm md:text-base"
              placeholder="Enter holder name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Loan Status</label>
            <select
              value={searchParams.loanStatus}
              onChange={e => setSearchParams({ ...searchParams, loanStatus: e.target.value })}
              className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm md:text-base"
            >
              <option value="">All Status</option>
              {getLoanStatusOptions().map((option, index) => (
                <option key={`loan-status-${option.value}-${index}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-4 md:mt-6">
          <button
            onClick={handleReset}
            className="w-full sm:w-auto px-4 md:px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm md:text-base"
          >
            Reset
          </button>
          <button
            onClick={handleSearch}
            className="w-full sm:w-auto px-4 md:px-6 py-2 bg-indigo-900 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm md:text-base"
          >
            Search
          </button>
        </div>
      </div>

      {/* Grid/Table Section */}
      {showGrid && (
        <SortableGrid
          data={filteredLoans}
          columns={loanColumns}
          loading={loading}
          emptyMessage="No loans found matching your search criteria."
          className="mb-4 md:mb-6"
          sortConfig={sortConfig}
          onSort={handleSort}
          expandedRows={expandedRow ? { [expandedRow]: true } : {}}
          renderExpandedContent={(loan) => (
            <div>
              {/* Payment Details Section */}
              <div className="mb-2 md:mb-4">
                            {/* Payment summary buttons */}
                            <div className="grid grid-cols-2 md:flex gap-2 md:gap-3 mb-2 md:mb-4">
                              <button className="px-2 md:px-4 py-1 md:py-2 bg-blue-500 text-white rounded-md font-bold text-xs md:text-sm" title="Principal (Loan Amount)">
                                Principal: ₹{paymentSummary ? paymentSummary.loanAmount.toLocaleString() : '0'}
                              </button>
                              <button className="px-2 md:px-4 py-1 md:py-2 bg-yellow-400 text-gray-900 rounded-md font-bold text-xs md:text-sm" title="Interest Amount">
                                Interest: ₹{paymentSummary ? paymentSummary.interestAmount.toLocaleString() : '0'}
                              </button>
                              <button className="px-2 md:px-4 py-1 md:py-2 bg-gray-800 text-white rounded-md font-bold text-xs md:text-sm" title="Total Amount to be Paid">
                                Total: ₹{paymentSummary ? paymentSummary.totalAmount.toLocaleString() : '0'}
                              </button>
                              <button className="px-2 md:px-4 py-1 md:py-2 bg-green-500 text-white rounded-md text-xs md:text-sm" title="Total Paid Amount">
                                Paid: ₹{paymentSummary ? paymentSummary.totalPaid.toLocaleString() : '0'}
                              </button>
                              <button className="px-2 md:px-4 py-1 md:py-2 bg-red-500 text-white rounded-md text-xs md:text-sm" title="Remaining Amount">
                                Remaining: ₹{paymentSummary ? paymentSummary.totalRemaining.toLocaleString() : '0'}
                              </button>
                            </div>
                          </div>
                          {/* Payment History Table */}
                          <div className="overflow-x-auto mb-4 md:mb-6">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="bg-gray-100">
                                  <th className="border border-gray-300 px-2 md:px-4 py-2 md:py-3 text-left text-xs md:text-sm font-medium text-gray-700">Payment Type</th>
                                  <th className="border border-gray-300 px-2 md:px-4 py-2 md:py-3 text-left text-xs md:text-sm font-medium text-gray-700">Amount Paid</th>
                                  <th className="border border-gray-300 px-2 md:px-4 py-2 md:py-3 text-left text-xs md:text-sm font-medium text-gray-700">Payment Date</th>
                                  <th className="border border-gray-300 px-2 md:px-4 py-2 md:py-3 text-left text-xs md:text-sm font-medium text-gray-700">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {paymentHistory.length > 0 ? paymentHistory.map((payment, idx) => (
                                  <tr key={payment.id || idx} className="hover:bg-gray-50">
                                    <td className="border border-gray-300 px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm">
                                      {getPaymentTypeOptions().find(option => option.value === payment.paymentType)?.label || payment.paymentType}
                                    </td>
                                    <td className="border border-gray-300 px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm">₹{payment.paymentAmount?.toLocaleString() || 0}</td>
                                    <td className="border border-gray-300 px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm">{formatDate(payment.paymentDate)}</td>
                                    <td className="border border-gray-300 px-2 md:px-4 py-2 md:py-3 text-xs md:text-sm">
                                      <button
                                        onClick={() => handleEditPayment(payment)}
                                        className="flex items-center gap-1 px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-xs"
                                        title="Edit payment"
                                      >
                                        <Edit size={12} />
                                        Edit
                                      </button>
                                    </td>
                                  </tr>
                                )) : (
                                  <tr><td colSpan={4} className="text-center py-2 md:py-4 text-gray-500 text-xs md:text-sm">No previous payments found</td></tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          
                          {/* Payment Receipt Button */}
                          <div className="flex justify-end mb-4">
                            <button
                              onClick={handlePaymentReceipt}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                              title="Generate Payment Receipt"
                            >
                              <Download size={16} />
                              Payment Receipt
                            </button>
                          </div>
                          
                          {/* Payment Form below the table */}
                          <div className="w-full">
                            <h3 className="text-base md:text-lg font-semibold mb-2">
                              {paymentData.id === 0 ? 'Make Payment' : 'Edit Payment'}
                            </h3>
                            {!showPaymentForm ? (
                              <button
                                onClick={handlePayClick}
                                className="px-4 md:px-6 py-2 bg-indigo-900 text-white rounded-md hover:bg-indigo-800 transition-colors text-sm md:text-base"
                              >
                                Pay
                              </button>
                            ) : (
                              <div className="space-y-3 md:space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Loan Number</label>
                                    <input
                                      type="text"
                                      value={paymentData.loanNumber}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed text-sm md:text-base"
                                      disabled
                                      readOnly
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date</label>
                                    <input
                                      type="date"
                                      value={paymentData.paymentDate}
                                      onChange={(e) => setPaymentData({...paymentData, paymentDate: e.target.value})}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm md:text-base"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Type</label>
                                    <select
                                      value={paymentData.paymentType}
                                      onChange={e => setPaymentData({ ...paymentData, paymentType: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm md:text-base"
                                    >
                                      <option value="">Select Payment Type</option>
                                      {getPaymentTypeOptions().map((option, index) => (
                                        <option key={`payment-type-${option.value}-${index}`} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Amount</label>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      onWheel={(e) => e.target.blur()}
                                      value={paymentData.paymentAmount}
                                      onChange={(e) => setPaymentData({...paymentData, paymentAmount: e.target.value})}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm md:text-base"
                                      placeholder="Enter amount"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                      Remaining {getPaymentTypeOptions().find(option => option.value === paymentData.paymentType)?.label || paymentData.paymentType}: ₹{getRemainingByType(paymentData.paymentType).toLocaleString()}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-end">
                                  {paymentData.id !== 0 && (
                                    <button
                                      onClick={handleCancelEdit}
                                      className="w-full sm:w-auto px-4 md:px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm md:text-base"
                                    >
                                      Cancel
                                    </button>
                                  )}
                                  <button
                                    onClick={handleResetPayment}
                                    className="w-full sm:w-auto px-4 md:px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm md:text-base"
                                  >
                                    Reset
                                  </button>
                                  <button
                                    onClick={handleSubmitPayment}
                                    disabled={loading}
                                    className="w-full sm:w-auto px-4 md:px-6 py-2 bg-indigo-900 text-white rounded-md hover:bg-indigo-800 transition-colors disabled:opacity-50 text-sm md:text-base"
                                  >
                                    {loading ? 'Submitting...' : (paymentData.id === 0 ? 'Submit' : 'Update')}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    />
                    )}
    </div>
  );
};

export default LoanInterestUI;